import { useQueryClient } from '@tanstack/react-query';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import ArrowBack from '@mui/icons-material/ArrowBack';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getHostingSitesV2QueryKey,
  getSitesListV2QueryKey,
  useHostingSiteAddV2,
  useSitesInfo,
  useSitesSecuritySslStatus,
  useSitesSso,
} from '@newfold/huapi-js';
import {
  HostingSiteAddV2Body,
  HostingSitesV2200ItemsItem,
  SitesSsoBounceName,
} from '@newfold/huapi-js/src/index.schemas';

import useAlertStore from '~/components/Alerts/alertsStore';
import { invalidateMultipleQueries } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import useHostingNavigate from '~/hooks/useHostingNavigate';
import LaunchMigrationModal from '~/scenes/AddSetupSiteFlows/components/LaunchMigrationModal';
import TransferOptions from '~/scenes/AddSetupSiteFlows/components/TransferOptions';
import TransferSiteV2 from '~/scenes/AddSetupSiteFlows/components/TransferSiteV2';
import WordPressIcon from '~/scenes/Sites/assets/icons/WordPressIcon';
import { canonicalizeUrl } from '~/utils/site/helpers';

import ErrorModal from '../../../AddSetupSiteFlows/components/ErrorModal';
import MultiStepCard from '../../../AddSetupSiteFlows/components/MultiStepCard';
import UpsellResourcesBanner from '../../components/UpsellResourcesBanner';
import SiteInstalledModal from './components/SiteInstalledModal';
import ChooseResources from './scenes/ChooseResources';
import ChooseSite from './scenes/ChooseSite';
import ChooseTitle from './scenes/ChooseTitle';
import ChooseType from './scenes/ChooseType';
import ConnectDomain from './scenes/ConnectDomain';

export type FormDataType = HostingSiteAddV2Body & {
  autoGeneration?: boolean;
  action?: string;
};

export const defaultFormData = {
  subtype: 'wordpress',
  title: '',
  admin_email: '',
  admin_user: '',
  datacenter: undefined,
  disk_limit_gib: 10,
  workers: 2,
  domain: '',
  path: '',
  autoGeneration: false,
  clone_site_id: undefined,
  migration_id: '',
  action: '',
} as FormDataType;

const CloudAddSite = ({
  siteList = undefined,
}: {
  siteList?: HostingSitesV2200ItemsItem[];
}) => {
  const [, { generateAlert }] = useAlertStore();
  const { t } = useTranslation(['addSites', 'errors']);
  const { id: hostingId } = useAccount();
  const navigate = useNavigate();
  const hostingNavigate = useHostingNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { handleErrors } = useErrorHandler();

  const { brandFromJWT: brand } = useBrandInfo();

  const [newSiteId, setNewSiteId] = useState();
  const [changeLoading, setChangeLoading] = useState(false);
  const [openSiteInstalledModal, setOpenSiteInstalledModal] = useState(false);
  const [existingSiteInfo, setExistingSiteInfo] =
    useState<HostingSitesV2200ItemsItem>({});
  const [openLaunchModal, setOpenLaunchModal] = useState(false);
  const [sslStatusRefetchCount, setSslStatusRefetchCount] = useState(0);

  const localStorageKey = `/${hostingId}/sites/add`;
  const isTransferPage = location?.pathname?.endsWith('/add/transfer');

  const hostingSitesQueryKey = getHostingSitesV2QueryKey(hostingId);
  const sitesListQueryKey = getSitesListV2QueryKey();
  const queryKeys = useMemo(
    () => [hostingSitesQueryKey, sitesListQueryKey],
    [hostingSitesQueryKey, sitesListQueryKey],
  );

  const [formData, setFormData] = useLocalStorage(
    localStorageKey,
    defaultFormData,
  );

  const methods = useForm({ defaultValues: formData });

  const hookOptions = {
    query: {
      refetchOnWindowFocus: false,
      enabled: false,
    },
  };

  const params = {
    bounce_name: SitesSsoBounceName.wpmigration,
  };

  const { refetch: fetchSso } = useSitesSso(
    Number(newSiteId),
    params,
    hookOptions,
  );

  const launchMigration = () => {
    setChangeLoading(false);
    setOpenLaunchModal(true);
  };

  const retryMigration = (retryFunc: any) => {
    return generateAlert({
      title: t('migrateSiteOptions.onError.title'),
      description: (
        <Trans
          i18nKey="addSites:migrateSiteOptions.onError.description"
          values={{ phoneNumber: '1-888-401-4678' }}
          components={{ Link: <Link href={'tel:+18884014678'}></Link> }}
        />
      ),
      severity: 'error',
      showCloseBtn: true,
      ctaText: t('migrateSiteOptions.onError.ctaText'),
      cta: retryFunc,
      autoHideDuration: 10000,
    });
  };

  const redirectToWPMigrationUrl = async () => {
    const wordpressSsoLoadingPage =
      brand === 'bluehost'
        ? `${process.env.PUBLIC_URL}/loading/wordpress-${brand}.html`
        : `${process.env.PUBLIC_URL}/loading/wordpress.html`;

    // Actually open the window
    var ssoWindow = window.open(wordpressSsoLoadingPage, '_blank');
    setChangeLoading(true);

    const { data, error } = await fetchSso();

    if (error) {
      // close the window
      ssoWindow?.close();
      setChangeLoading(false);
      return retryMigration(() => redirectToWPMigrationUrl());
    }

    const url = data?.data?.sso;

    if (url && ssoWindow) {
      // set url for window
      ssoWindow.location = url;

      setChangeLoading(false);
      setOpenLaunchModal(false);
      hostingNavigate();
      return;
    }

    ssoWindow?.close();

    generateAlert({
      severity: 'error',
      description: t('ssoLink.notFound'),
      showCloseBtn: true,
    });
    setOpenLaunchModal(false);
    setChangeLoading(false);
  };

  const refetchSslStatusMaxAttempts = 6;
  const enableSslStatusRefetch =
    sslStatusRefetchCount > 0 &&
    sslStatusRefetchCount <= refetchSslStatusMaxAttempts;

  // https://jira.newfold.com/browse/HOST-1807
  // Need to check SSL status before allowing the user to SSO into WordPress
  const {
    data: sslData,
    refetch: refetchSitesSslStatus,
    isSuccess: isSuccessSSL,
    isError: isErrorSSL,
  } = useSitesSecuritySslStatus(
    Number(newSiteId),
    { domain_scan: true },
    {
      query: {
        enabled: enableSslStatusRefetch,
        refetchInterval: (query) => {
          const sslStatus = query?.state?.data?.data?.ssl_status;
          if (
            sslStatusRefetchCount <= refetchSslStatusMaxAttempts &&
            sslStatus !== 'active'
          ) {
            return 20000;
          }
          return false;
        },
      },
    },
  );

  // onSuccess - useSitesSecuritySslStatus
  useEffect(() => {
    if (isSuccessSSL) {
      setSslStatusRefetchCount((old) => old + 1);
      if (sslData?.data?.ssl_status === 'active') {
        setSslStatusRefetchCount(refetchSslStatusMaxAttempts + 1);
        launchMigration();
      }
    }
  }, [isSuccessSSL, sslData]);

  // onError - useSitesSecuritySslStatus
  useEffect(() => {
    if (isErrorSSL || sslStatusRefetchCount >= refetchSslStatusMaxAttempts) {
      generateAlert({
        title: t('migrateSiteOptions.sslError.title'),
        description: t('migrateSiteOptions.sslError.description'),
        severity: 'error',
        showCloseBtn: true,
        persist: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorSSL, sslData]);

  const handleSiteInstalled = useCallback(() => {
    if (isTransferPage) return refetchSitesSslStatus();
    setOpenSiteInstalledModal(true);
    setChangeLoading(false);
  }, [isTransferPage, refetchSitesSslStatus]);

  const handleOnClose = () => {
    setExistingSiteInfo({});
  };

  // Watches for changes in the form to keep localStorage in sync
  // Also helpful for form reset
  useEffect(() => {
    const subscription = methods.watch((value) =>
      setFormData({ ...value } as any),
    );
    return () => subscription.unsubscribe();
  }, [methods, methods.watch, setFormData]);

  useEffect(() => {
    (!formData?.subtype || !formData?.title) &&
      location?.pathname !== '/sites/add' &&
      location?.pathname !== '/package/websites/add' &&
      !location?.pathname?.endsWith('/package/websites/add') &&
      !location?.pathname?.endsWith('/sites/add') &&
      navigate('');
    // Need to comment out lint rule to ensure function only runs ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchSiteCount = useRef(0);
  const refetchSiteLimit = 30;

  const installingStatuses = [
    'pending_create',
    'create',
    'pending_configure',
    'configure',
  ];

  const installFailedStatuses = useMemo(
    () => ['failed_create', 'failed_configure'],
    [],
  );

  const {
    data: siteInfo,
    refetch: refetchSiteInfo,
    isSuccess: isSuccessSiteInfo,
  } = useSitesInfo(Number(newSiteId), {
    query: {
      enabled: !!newSiteId,
      refetchInterval: (query) => {
        const status = query?.state?.data?.data?.status!;
        const siteInstallSlow = refetchSiteCount.current >= refetchSiteLimit;
        return (installingStatuses.includes(status) &&
          !installFailedStatuses.includes(status) &&
          !siteInstallSlow) ||
          (isTransferPage && status !== 'active')
          ? 5000
          : false;
      },
    },
  });

  // onSuccess - useSitesInfo
  useEffect(() => {
    if (isSuccessSiteInfo) {
      (async () => {
        const siteInstallSlow = refetchSiteCount.current >= refetchSiteLimit;
        const siteStatus = siteInfo?.data?.status;
        if (siteStatus === 'active') {
          await invalidateMultipleQueries(queryClient, queryKeys);
          handleSiteInstalled();
          return siteInfo;
        }
        // If site is failed to create/configure, redirect to site list page
        // OR
        // If site is NOT active yet, retry up to 30 times in 5 second intervals
        // If site is still NOT active, redirect to site list page
        refetchSiteCount.current += 1;
        if (
          (siteStatus && installFailedStatuses.includes(siteStatus)) ||
          siteInstallSlow
        ) {
          generateAlert({
            title: t('alertCreatingSite.title'),
            description: t('alertCreatingSite.desc'),
            severity: 'info',
            showCloseBtn: true,
          });
          setChangeLoading(false);
          hostingNavigate();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessSiteInfo, siteInfo]);

  const { mutate: createWebsite } = useHostingSiteAddV2({
    mutation: {
      onMutate: () => setChangeLoading(true),
      onSuccess: async (data) => {
        await invalidateMultipleQueries(queryClient, queryKeys);
        if (formData.migration_id) {
          setTimeout(() => {
            hostingNavigate();
          }, 10000);
        } else {
          //@ts-expect-error
          setNewSiteId(data?.data?.site_id as string);
          // will trigger site info call to check site status after 10 seconds
          setTimeout(() => refetchSiteInfo(), 10000);
        }
        localStorage.removeItem(localStorageKey);
      },
      onError: (error: any) => {
        if (formData?.migration_id) {
          generateAlert({
            severity: 'error',
            description: t('addSites:migrateSiteOptions.transferError'),
            showCloseBtn: true,
            persist: true,
          });
        } else if (error?.response?.data?.error === 'urlInUse') {
          // TODO: Get site info from BE error response to avoid looping through site list
          const existingSite =
            siteList &&
            siteList.find((site: HostingSitesV2200ItemsItem) =>
              canonicalizeUrl(site?.url!).endsWith(
                canonicalizeUrl(`://${formData.domain}/${formData.path}`),
              ),
            );
          if (existingSite) {
            setExistingSiteInfo(existingSite);
          } else {
            generateAlert({
              title: t('alert.urlInUseTitle'),
              description: t('alert.urlInUseDesc', {
                path: canonicalizeUrl(`${formData.domain}/${formData.path}`),
              }),
              severity: 'error',
              showCloseBtn: true,
              persist: true,
            });
          }
        } else if (isTransferPage) {
          setChangeLoading(false);
          retryMigration(() =>
            createWebsite({
              hostingId,
              data: {
                ...(!!formData.workers && {
                  workers: formData.workers,
                }),
                ...(!!formData.datacenter && {
                  datacenter: formData.datacenter,
                }),
                ...(!!formData.disk_limit_gib && {
                  disk_limit_gib: Number(formData?.disk_limit_gib),
                }),
                subtype: 'wordpress',
              } as any,
            }),
          );
        } else {
          handleErrors(error);
        }
        setChangeLoading(false);
      },
    },
  });

  const onSubmit = (data: any) => {
    createWebsite({
      hostingId,
      data: {
        title: data.title,
        admin_email: data.admin_email,
        admin_user: data.admin_user,
        datacenter: data?.datacenter,
        disk_limit_gib: Number(data.disk_limit_gib),
        workers: Number(data.workers),
        subtype: data.subtype,
        domain: data.domain,
        ...(!!data.clone_site_id && { clone_site_id: data.clone_site_id }),
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <Container data-testid="cloud-add-site-flow">
        <SiteInstalledModal
          open={openSiteInstalledModal}
          newSiteId={newSiteId}
        />
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          margin={2}
        >
          <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          <Typography variant="h1">
            {t('addSites:addSiteOptions.add')}
          </Typography>
          <WordPressIcon style={{ width: '30px', height: '30px' }} />
          <Typography variant="h1">
            {t('addSites:addSiteOptions.wordpress')}
          </Typography>
        </Stack>
        <Stack spacing={2}>
          <MultiStepCard>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Routes>
                <Route index element={<ChooseType formData={formData} />} />
                <Route
                  path="site/*"
                  element={<ChooseSite formData={formData} />}
                />
                <Route
                  path="title/*"
                  element={
                    <ChooseTitle
                      formData={formData}
                      changeLoading={changeLoading}
                    />
                  }
                />
                <Route
                  path="connect/*"
                  element={
                    <ConnectDomain
                      formData={formData}
                      setFormData={setFormData}
                      changeLoading={changeLoading}
                    />
                  }
                />
                <Route
                  path="config/*"
                  element={
                    <ChooseResources
                      formData={formData}
                      createWebsite={createWebsite}
                      changeLoading={changeLoading}
                    />
                  }
                />
                <Route
                  path="transfer/*"
                  element={<TransferSiteV2 changeLoading={changeLoading} />}
                />
              </Routes>
            </form>
          </MultiStepCard>
          {(location?.pathname?.endsWith('/add/title') ||
            location?.pathname?.endsWith('/add/config')) && (
            <UpsellResourcesBanner
              title={t('resourcesBanner.title')}
              desc={t('resourcesBanner.description')}
              isCloneWP={false}
            />
          )}
        </Stack>
      </Container>
      {isTransferPage && <TransferOptions />}
      <ErrorModal
        open={!!existingSiteInfo?.id}
        errorTitle={t('urlTaken')}
        paragraphOne={
          <Trans
            i18nKey="addSites:alreadySiteName"
            values={{
              path: existingSiteInfo?.url,
            }}
          />
        }
        paragraphTwo={
          <Trans
            i18nKey="addSites:delete"
            values={{ name: existingSiteInfo?.name }}
            components={{
              Link: (
                <Link href={`/sites/${existingSiteInfo?.id}/settings`}></Link>
              ),
            }}
          />
        }
        handleOnClose={handleOnClose}
      />
      <LaunchMigrationModal
        open={openLaunchModal}
        isPending={changeLoading}
        handleContinue={redirectToWPMigrationUrl}
      />
    </FormProvider>
  );
};

export default CloudAddSite;
